// Phase 9.6 — Portal project-request API client (2026-05-12).
//
// Thin wrappers over POST + GET /portal/projects/request. Submission is
// idempotent on the server side (each submit creates a new row); the
// frontend prevents double-clicks via useMutation's `isPending` state.
import { api } from "@/lib/api";

export type ProjectRequestStatus = "pending" | "converted" | "dismissed";

export interface PortalProjectRequest {
  id: string;
  projectName: string;
  productCategory: string | null;
  budgetRange: string | null;
  deadline: string | null;
  notes: string | null;
  status: ProjectRequestStatus;
  createdAt: string;
}

export interface SubmitProjectRequestArgs {
  project_name: string;
  product_category?: string | null;
  budget_range?: string | null;
  deadline?: string | null;
  notes?: string | null;
  /** Ids from uploadRequestFile (max 10). */
  attachment_file_ids?: string[];
  /** Picks from the re-order list — which item + optional quantity only. */
  reorder_items?: Array<{ line_item_id: string; quantity?: number | null }>;
}

export async function submitPortalProjectRequest(
  args: SubmitProjectRequestArgs,
): Promise<{ request_id: string }> {
  return api<{ request_id: string }>("/portal/projects/request", {
    method: "POST",
    body: JSON.stringify(args),
  });
}

export async function fetchPortalProjectRequests(): Promise<{
  rows: PortalProjectRequest[];
}> {
  return api<{ rows: PortalProjectRequest[] }>(
    "/portal/projects/request",
  );
}

// ─── Attachments + re-order (CEO 2026-10-19) ─────────────────────────

/** A product from one of this client's finished orders. Names and photos
 *  only — the picker never carries a price. */
export interface ReorderOption {
  line_item_id: string;
  name: string;
  image_url: string | null;
  last_quantity: number;
  last_order_number: number;
  last_ordered_at: string | null;
}

export interface RequestFormOptions {
  /** False until the database step runs — the form hides the file picker. */
  attachments_enabled: boolean;
  /** False until the database step runs — the form hides the re-order picker. */
  reorder_enabled: boolean;
  max_files: number;
  max_file_bytes: number;
  reorder_items: ReorderOption[];
}

const OPTIONS_OFF: RequestFormOptions = {
  attachments_enabled: false,
  reorder_enabled: false,
  max_files: 10,
  max_file_bytes: 50 * 1024 * 1024,
  reorder_items: [],
};

/** What the form may offer. Any failure → the plain form, no error shown. */
export async function fetchRequestFormOptions(): Promise<RequestFormOptions> {
  try {
    return await api<RequestFormOptions>("/portal/projects/request/options");
  } catch {
    return OPTIONS_OFF;
  }
}

/**
 * Upload one attachment: presign, then PUT the bytes straight to R2 (same
 * two-step flow as the brand-assets upload). Returns the file id to send
 * with the request. Throws on any failure so the form can show one message.
 */
export async function uploadRequestFile(file: File): Promise<{ fileId: string }> {
  const contentType = file.type || "application/octet-stream";
  const presign = await api<{ file_id: string; upload_url: string; live: boolean }>(
    "/portal/projects/request/files",
    {
      method: "POST",
      body: JSON.stringify({
        filename: file.name,
        mime_type: contentType,
        size_bytes: file.size,
      }),
    },
  );
  if (presign.live) {
    const put = await fetch(presign.upload_url, {
      method: "PUT",
      headers: { "Content-Type": contentType },
      body: file,
    });
    if (!put.ok) throw new Error(`Upload failed (${put.status})`);
  }
  return { fileId: presign.file_id };
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── Display helpers ─────────────────────────────────────────────────

export const STATUS_LABEL: Record<ProjectRequestStatus, string> = {
  pending: "Awaiting review",
  converted: "Started",
  dismissed: "Closed",
};

export const STATUS_PILL_CLASS: Record<ProjectRequestStatus, string> = {
  pending: "bg-peach-100 text-brown-500",
  converted: "bg-ok-bg text-ok",
  dismissed: "bg-cream-200 text-brown-400",
};

/** Common product categories — keeps the form picker tight. "Other"
 *  catches anything not on the list. */
export const PRODUCT_CATEGORIES = [
  "Apparel (T-shirts / hoodies / outerwear)",
  "Headwear (hats / caps / beanies)",
  "Drinkware (mugs / bottles / tumblers)",
  "Bags & totes",
  "Tech & accessories",
  "Office & desk",
  "Notebooks & writing",
  "Event swag (one-off)",
  "Hard goods",
  "Other",
] as const;
